import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CourierWeightFilesApiAdapter } from '../adapters';
import { extractApiErrorMessage } from '../lib/extractApiErrorMessage';
import type { UploadCourierWeightFileRequest, UploadCourierWeightFileResponse } from '../models';

export const useUploadCourierWeightFile = () => {
  const { toast } = useToast();
  const { t } = useTranslation('dataLoadModule');

  const mutation = useMutation({
    mutationFn: async (data: UploadCourierWeightFileRequest): Promise<UploadCourierWeightFileResponse> => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new CourierWeightFilesApiAdapter(httpClient);
      return adapter.uploadCourierWeightFile(data);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        if (error.message === 'INVALID_FILE_TYPE') {
          toast({
            title: t('upload.error.title', 'Error al subir archivo'),
            description: t('upload.error.invalid_type', 'Formato no permitido. Usa .xlsx o .xls.'),
            variant: 'destructive',
          });
          return;
        }

        if (error.message === 'FILE_TOO_LARGE') {
          toast({
            title: t('upload.error.title', 'Error al subir archivo'),
            description: t('upload.error.too_large', 'El archivo supera el límite de 10 MB.'),
            variant: 'destructive',
          });
          return;
        }
      }

      const errorMessage = extractApiErrorMessage(error, t('upload.error.unexpected', 'Ocurrió un error inesperado'));

      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  return {
    uploadCourierWeightFileAsync: mutation.mutateAsync,
    isUploading: mutation.isPending,
  };
};
